import { BaseComponentProps } from '@/pages/benchmark/types/commom';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import React, { useEffect, useState } from 'react';
import { Button, Input } from 'tea-component';
import { getLocale } from 'umi';
import { getGamePercentage } from '@/pages/benchmark/services/benchmark';
import classNames from 'classnames';
import { dynamicString } from '@home/utils/dynamicString';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import './index.scss';

/**
 * IMetricDetailTableSearchBoxProps 接口定义了 MetricDetailTableSearchBox 组件的属性类型。
 * 
 * @property {string} textEn - 英文文本。
 * @property {string} textCn - 中文文本。
 * @property {string} resultEn - 英文结果文本。
 * @property {string} resultCn - 中文结果文本。
 * @property {string} errorTextEn - 英文错误文本。
 * @property {string} errorTextCn - 中文错误文本。
 * @property {string} type - 类型标识。
 * @property {any} filter - 过滤条件。
 */
export interface IMetricDetailTableSearchBoxProps extends BaseComponentProps {
  textEn: string;
  textCn: string;
  resultEn: string;
  resultCn: string;
  errorTextEn: string;
  errorTextCn: string;
  percentErrorTextEn: string;
  percentErrorTextCn: string;
  type: string;
  filter: any;
}

const MetricDetailTableSearchBox = (
  props: IMetricDetailTableSearchBoxProps,
) => {
  const {
    textEn,
    textCn,
    resultEn,
    resultCn,
    errorTextEn,
    errorTextCn,
    percentErrorTextEn,
    percentErrorTextCn,
    filter,
    type
  } = props;
  const isEn = getLocale() === LANGUAGE_EN;
  const isPercent = type === 'percent';

  const [isError, setIsError] = useState(false);
  const [isPercentError, setIsPercentError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!searchText) {
      setIsError(false);
      setValue('');
      return;
    }
    if (!searchText?.trim()) {
      setIsError(true);
      setValue('');
      return;
    }
    // 校验是否纯数字或者小数
    if (/^\d+(\.\d+)?$/.test(searchText)) {
      setIsError(false);
    } else {
      setIsError(true);
      setIsPercentError(false);
      return;
    }

    // 百分比数值需要判断是否超过100
    if (isPercent) {
      if (Number(searchText) > 100) {
        setIsPercentError(true);
      } else {
        setIsPercentError(false);
      }
    }
  }, [searchText]);

  useEffect(() => {
    setIsError(false);
    setIsLoading(false);
    setSearchText('');
    setValue('');
  }, [filter]);

  const getResult = () => {
    setIsLoading(true);
    const currentMetric = filter?.metrics?.[0];
    const newFilter = {
      ...filter,
      // 百分比指标需要/100
      metric_values: { [currentMetric]: type === 'percent' ? String(Number(searchText) / 100) : searchText },
    };
    getGamePercentage(newFilter)
      .then((data) => {
        const value = data?.metrics?.[currentMetric]?.percentile;
        setValue(dataFormat({ value: Number(value) * 100, formatRule: 'oneDecimal' }) as string);
      })
      .finally(() => setIsLoading(false));
  };

  let showResult = null;
  if (isPercent) {
    showResult = value && !isError && !isPercentError;
  } else {
    showResult = value && !isError;
  }

  return (
    <div className="dbb-metric-detail-search-container">
      <div className="dbb-metric-detail-search-text">
        {getName(isEn, textEn, textCn)}
      </div>
      <Input
        value={searchText}
        onChange={(value) => {
          setSearchText(value);
        }}
        onPressEnter={() => {
          if (isPercent) {
            if (searchText && !isError && !isPercentError) {
              getResult();
            }
          } else {
            if (searchText && !isError) {
              getResult();
            }
          }
        }}
      />
      {type === 'percent' && <span className='dbb-metric-detail-search-percent'>%</span>}
      <Button
        type="primary"
        onClick={getResult}
        loading={isLoading}
        disabled={!searchText || isError || (isPercent && isPercentError)}
      >
        {getName(isEn, 'Confirm', '确定')}
      </Button>
      {isError && (
        <div className="dbb-metric-detail-search-error-text">
          {getName(isEn, errorTextEn, errorTextCn)}
        </div>
      )}
      {isPercent && isPercentError && (
        <div className="dbb-metric-detail-search-error-text">
          {getName(isEn, percentErrorTextEn, percentErrorTextCn)}
        </div>
      )}
      {showResult && (
        <div
          className={classNames('dbb-metric-detail-search-value-text', {
            'dbb-metric-detail-search-value-then-60': Number(value) >= 60,
          })}
        >
          {getName(isEn, dynamicString(resultEn, { value }), dynamicString(resultCn, { value }))}
        </div>
      )}
    </div>
  );
};
export default MetricDetailTableSearchBox;
